import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import SearchPageSortSection from './search-page-sort-section';
import SearchPageHeader from './search-page-header';
import SearchProductBanner from './search-product-banner';
import SearchPageLoadMore from './search-page-load-more';
import VWALeftMenu from '../../vwa/components/vwa-left-menu';
import CompareDrawer from '../../compare/components/compare-drawer';
import SearchProductPriceFilter from './search-page-product-price-filter';
import SearchProductSwatchFilter from './search-page-product-swatch-filter';
import SearchProductRatingFilter from './search-page-product-rating-filter';
import SearchPageMobileLeftMenu from './search-page-mobile-left-menu';
import SearchSupportStaticTiles from './search-support-static-tiles';

import { formatMetricValue } from '../../metrics/metrics-helpers';
import useHawksearchActions from '../useHawksearchActions';
import useHawksearchResults from '../useHawksearchResults';
import useHawksearchComponent, { useSearchAttempted } from '../useHawksearchComponent';
import useSupport from '../useSupport';
import withPageComponents from '../../page/components/with-page-components';
import useUserData, { getPStoreID } from '../../hooks/useUserData';

import './css/search-page-template.less';

/**
 *
 * @param {*} facets - Facets data returned from hawksearch
 * @returns parsed facets array used for reusable FilterMenu component.
 */
const mapFacets = (facets, aggregatedSelections, options) => {
    let { onSelectFacet, priceRangeSelector, setSearch, facetSelector, currentSearch } = options || {};
    let expandedFacets = [];
    let facetCount = 0;
    let selectedFacetCount = 0;
    let transformedFacets = Array.isArray(facets)
        ? facets.reduce((allFacets, facet) => {
              let {
                  Name,
                  Values,
                  FacetId,
                  Field,
                  ExpandSelection,
                  Tooltip,
                  TruncateThreshold,
                  DisplayType,
                  FacetType,
                  IsCurrency,
                  FieldType,
                  Ranges,
                  IsCollapsedDefault,
                  IsCollapsible,
              } = facet || {};
              let validName = typeof Name === 'string';
              let facetProps = {};

              // hide single facets if it's not selected, and the tab facet
              if (!facet || (validName && /^tabs$/i.test(Name))) return allFacets;

              let hasSelectedFacetValue = false;

              // show the clear filter button and expand by default if range is specified for price sliders.
              if (IsCurrency && FacetType === 'slider') {
                  let rangeValue = Values && Values[0];
                  let { RangeMin, RangeMax, RangeEnd, RangeStart } = rangeValue || {};
                  hasSelectedFacetValue = RangeMin !== RangeStart || RangeEnd !== RangeMax;
                  selectedFacetCount += hasSelectedFacetValue ? 1 : 0;
                  className = 'srp-price-slider';
              }

              let FilterComponent;
              let FilterSetComponent;
              let className;
              let accordionBodyClassName;
              if (typeof Name === 'string' && /^product color$/i.test(Name)) {
                  FilterComponent = SearchProductSwatchFilter;
                  className = 'swatch-group srp-swatch-filters';
                  accordionBodyClassName = 'srp-color-filter-accordion-body';
              }

              if (Field === 'dte_facet_ratings') {
                  FilterComponent = SearchProductRatingFilter;
              }

              if (IsCurrency) {
                  FilterSetComponent = SearchProductPriceFilter;
                  facetProps = {
                      priceRangeFilter: facet,
                      priceRangeSelector,
                      hasAccordion: false,
                      Ranges,
                      FieldType,
                      FacetType,
                      setSearch,
                      onSelectFacet,
                      facetSelector,
                      currentSearch,
                  };
              }

              const truncateThreshold = DisplayType === 'truncating' && TruncateThreshold;
              const facetValues = Values.map(fV => {
                  let { Label, Selected, Count, Value, facetKey } = fV || {};
                  let isSelectedForAggregateQuery =
                      aggregatedSelections && aggregatedSelections[Field] && aggregatedSelections[Field][Value]
                          ? true
                          : false;
                  let isSelected = isSelectedForAggregateQuery || Selected;
                  hasSelectedFacetValue = isSelected || hasSelectedFacetValue;

                  isSelected && selectedFacetCount++;
                  return {
                      displayName: Label,
                      label: Label,
                      alias: Value,
                      key: Value,
                      urlValue: Value,
                      checked: isSelected + '',
                      options: {
                          selected: isSelected,
                      },
                      selected: isSelected,
                      count: Count,
                      facetKeyOverride: facetKey,
                      gtmActions: new Map([
                          [
                              'checkboxClick',
                              {
                                  gtmId: formatMetricValue(Name),
                                  gtmCategory: 'facet',
                                  gtmValue: formatMetricValue(Label),
                                  gtmChecked: !isSelected,
                              },
                          ],
                      ]),
                  };
              });

              facetCount += Values.length;
              // expanded if IsCollapsible=false, or if IsCollapsedDefault=false and IsCollapsible=true at the same time, or there's a facet selected
              let defaultExpanded = !IsCollapsible || !IsCollapsedDefault || hasSelectedFacetValue;
              if (defaultExpanded) expandedFacets.push(Field);

              allFacets.push({
                  displayName: Name,
                  alias: Field,
                  key: Field,
                  facetValues,
                  facetValue: facetValues,
                  defaultExpanded,
                  tooltip: Tooltip,
                  FilterComponent,
                  FilterSetComponent,
                  className,
                  truncateThreshold,
                  collapsedDefault: IsCollapsedDefault,
                  isCollapsible: IsCollapsible,
                  facetProps,
                  accordionBodyClassName,
              });

              return allFacets;
          }, [])
        : [];

    return {
        facets: transformedFacets,
        expandedFacets,
        facetCount,
        selectedFacetCount,
    };
};

const SearchPageTemplate = props => {
    const { onSwitchTabs, tabs, activeIndex, searchKey, currentTab, children, type } = props;

    const {
        selectedSortOption,
        sortingOptions,
        onSort,
        initialSearch,
        priceRangeSelector,
        clearFilters,
        facetSelector,
        store,
        aggregatedFilterSelections,
        toggleFacetValue,
        setSearch,
    } = useHawksearchResults();

    const hawksearchResults = useHawksearchComponent();
    const searchAttempted = useSearchAttempted();
    const isSupport = type === 'support';
    const isProduct = type === 'products';
    const isContent = type === 'content';

    const { query } = hawksearchResults || {};

    const hawksearchSearchData =
        hawksearchResults && (isContent ? hawksearchResults.content : hawksearchResults.products);

    const { gtmConfig } = currentTab || {};

    const { DidYouMean, TrackingId, currentSearch } = hawksearchSearchData || {};
    const isMobile = useSelector(
        state => state && state.ui && state.ui.customBreakpoints && state.ui.customBreakpoints.srp === 'mobile',
    );

    const isMobileSearchBarOpen = useSelector(
        state => state && state.headerFooter && state.headerFooter.mobileSearchBarOpen,
    );

    const { Pagination, Facets, Results: SearchResults, Sorting, Keyword } = hawksearchSearchData || {};
    const [currentFacets, setCurrentFacets] = useState({ Facets, type });
    const [expandedMobileMenu, setExpandedMobileMenu] = useState(false);
    const supportResults = useSupport(searchKey, type);
    const { supportPages, fetchError } = supportResults || {};
    useHawksearchActions(type, supportPages, searchKey);
    // prevents the facets from going away when result is 0
    useEffect(() => {
        if (Facets && Facets.length) {
            setCurrentFacets({ Facets, type });
        }
    }, [Facets]);

    useEffect(() => {
        if (!Facets || (Array.isArray(Facets) && Facets.length < 1)) {
            setCurrentFacets({ Facets, type });
        }
    }, [Keyword]);

    useEffect(() => {
        if (currentFacets.type !== type) {
            setCurrentFacets({ Facets, type });
        }
    }, [type]);

    const { userData } = useUserData();
    const pStoreID = getPStoreID(userData);

    // pagination
    const { NofResults, CurrentPage, NofPages, MaxPerPage } = Pagination || {};

    const zeroResults = isSupport ? supportPages && supportPages.length === 0 : NofResults === 0;

    const onSelectFacet = (facetKey, facetValue) => facetSelector(facetKey, facetValue, Facets);

    // TODO: MOVE THIS TO HAWKSEARCH HOOK
    const { facets, expandedFacets, facetCount, selectedFacetCount } = mapFacets(
        currentFacets && currentFacets.Facets,
        aggregatedFilterSelections,
        {
            onSelectFacet,
            priceRangeSelector,
            setSearch,
            facetSelector,
            currentSearch,
        },
    );
    const facetAggregationOverride = {
        facets,
    };

    const filterProps = {
        facets,
        expandedFacets,
        disableDefaultPriceRange: true,
        onSelectFacet,
        tooltipProps: {
            activeOnHover: false,
        },
        tooltipPosition: 'left',
    };
    const hasResults = SearchResults && SearchResults.length > 0;
    const searchCount = isSupport ? supportPages && supportPages.length : NofResults;
    const childProps = {
        setSearch,
        currentPage: CurrentPage,
        totalPages: NofPages,
        searchResults: SearchResults,
        Sorting,
        searchCount,
        sortingOptions,
        selectedSortOption,
        onSort,
        currentSearch,
        gtmConfig,
        Keyword,
        searchKey,
        hsResID: TrackingId,
        pStoreID,
        query,
        ...(isSupport ? { supportResults } : {}),
    };

    let loadMoreProps = {
        totalPages: NofPages,
        setSearch: searchQuery => setSearch(searchQuery, false), // don't update the URL
        currentPage: CurrentPage,
        gtmConfig,
    };

    let sortSectionProps = isSupport
        ? {
              resultsTextOnly: true,
              currentResultsCount: searchCount,
              searchKey,
              resultCount: searchCount,
              isSupport,
          }
        : {
              resultCount: searchCount,
              sortingOptions,
              selectedSortOption,
              onSort,
              aggregationOverride: facetAggregationOverride,
              customFilterClick: onSelectFacet,
              filterCount: selectedFacetCount,
              setModalState: setExpandedMobileMenu,
              isMobile,
              clearFilters,
              searchKey,
              isSupport,
              totalPageDisplayed: Math.min(MaxPerPage * CurrentPage, searchCount),
          };

    const headerProps = {
        onSwitchTabs,
        tabs,
        activeIndex,
        searchKey,
        searchCount,
        ...(isSupport && { tabType: 'support', supportResults }),
    };
    const containerClassName = isSupport
        ? ' search-support-container'
        : isContent
          ? ' search-content-container'
          : isProduct
            ? ' search-product-container'
            : '';
    const hasFacets = facets && facets.length > 0;
    const nonSupportNoFacets = (isProduct || isContent) && !hasFacets;
    const resultsAreReady = hawksearchSearchData || searchAttempted || (isSupport && (supportPages || fetchError))
    return (
        <>
            <div className={`srp-results-section${isMobileSearchBarOpen ? ' with-ac-mobile-search-open' : ''}${!resultsAreReady ? ' srp-loading' : ''}`}>
                <div className={`hawk${containerClassName}`}>
                    {!isSupport && <SearchProductBanner />}
                    <SearchPageHeader key="search-page-header" {...(headerProps || {})} />
                    <SearchPageSortSection {...sortSectionProps} />
                    <div
                        className={`hawk__body${nonSupportNoFacets ? ' srp-no-facet-template' : ''}${
                            ((isProduct || isContent) && !hasFacets) || !searchCount || Number(searchCount) < 0
                                ? ' srp-body-no-facets'
                                : ''
                        }`}
                    >
                        {!isSupport && !isMobile && hasResults && hasFacets && (
                            <VWALeftMenu {...filterProps} isSearch visId />
                        )}
                        <div className={`srp-result-tiles${isSupport ? ' srp-support-tiles' : ''}`}>
                            {zeroResults ? <SearchSupportStaticTiles pStoreID={pStoreID} /> : children(childProps)}
                            {!isSupport && Number(NofPages) > 0 && (
                                <div className="load-more">
                                    <SearchPageLoadMore {...loadMoreProps} />
                                </div>
                            )}
                        </div>
                    </div>
                    {!isSupport && (
                        <SearchPageMobileLeftMenu
                            filterProps={filterProps}
                            resultsCount={Pagination && Pagination.NofResults}
                            expandedMobileMenu={expandedMobileMenu}
                            sortingOptions={sortingOptions}
                            selectedSortOption={selectedSortOption}
                            clearFilters={clearFilters}
                            setExpandedMobileMenu={setExpandedMobileMenu}
                            onSort={onSort}
                            filterCount={selectedFacetCount}
                        />
                    )}
                </div>
            </div>
            {isProduct && <CompareDrawer />}
        </>
    );
};

export default withPageComponents(SearchPageTemplate, {
    components: ['hawksearchResults'],
});
